// frontend-participant/src/FieldUpdatesNotification.js
import React, { useState } from "react";
import "./FieldUpdatesNotification.css";

/**
 * FieldUpdatesNotification - Displays form field updates in an expandable box
 * 
 * @param {Object} props
 * @param {Array} props.updates - Array of field update objects
 * @param {Function} props.getFieldLabel - Function to get human-readable field label
 */
const FieldUpdatesNotification = ({ updates, getFieldLabel }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!updates || updates.length === 0) {
    return null;
  }

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Format value for display
  const formatValue = (value) => {
    if (value === null || value === undefined || value === "" || value === "open" || value === "<open>") {
      return <em className="empty-value">(not filled)</em>;
    }
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    if (Array.isArray(value)) {
      return value.join(", ");
    }
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    return String(value);
  };

  // Get badge class based on source
  const getBadgeClass = (source) => {
    return source === "active" ? "badge-active" : "badge-passive";
  };

  // Get badge text
  const getBadgeText = (source) => {
    return source === "active" ? "Current" : "Background";
  };

  return (
    <div className="field-updates-notification">
      <div 
        className="updates-header"
        onClick={toggleExpanded}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => e.key === "Enter" && toggleExpanded()}
      >
        <div className="updates-summary">
          <span className="updates-icon">📝</span>
          <span className="updates-count">
            {updates.length} form field{updates.length !== 1 ? "s" : ""} updated
          </span>
        </div>
        <button 
          className="expand-button"
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? "▼" : "▶"}
        </button>
      </div>

      {isExpanded && (
        <div className="updates-body">
          <ul className="updates-list">
            {updates.map((update, index) => {
              const fieldLabel = getFieldLabel 
                ? getFieldLabel(update.field_id) 
                : update.field_id;
              
              return (
                <li key={`${update.field_id}-${index}`} className="update-item">
                  <div className="update-field">
                    <div className="field-label-container">
                      <span className="field-label">{fieldLabel}</span>
                      {/* Show field ID if it's different from the label */}
                      {fieldLabel !== update.field_id && (
                        <span className="field-id">({update.field_id})</span>
                      )}
                    </div>
                    <span className={`source-badge ${getBadgeClass(update.source)}`}>
                      {getBadgeText(update.source)}
                    </span>
                  </div>
                  <div className="update-value">
                    {formatValue(update.value)}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FieldUpdatesNotification;
